import { useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/shell/AppShell';
import { GlobalHeader } from '@/components/shell/GlobalHeader';
import { MobileBottomNav } from '@/components/shell/MobileBottomNav';
import { SkipLink } from '@/components/shell/SkipLink';
import { ViewModeToggle } from '@/components/shell/ViewModeToggle';
import { HelpHeaderButton } from '@/modules/help/HelpHeaderButton';
import { TenantAuthProvider } from '@/components/providers/TenantAuthProvider';
import { useTimezone } from '@/hooks/useTimezone';
import { TenantViewModeProvider, useTenantViewMode } from '@/contexts/TenantViewModeContext';
import { logoutTenant, getTenantSession } from '@/lib/auth/tenant-auth';
import { useTenantMe } from '@/hooks/useTenantMe';
import { getTenantNavItems } from '@/lib/nav/tenant-view-nav';
import type { BreadcrumbSegment } from '@/types/nav';

function buildTenantBreadcrumb(pathname: string): BreadcrumbSegment[] {
  const parts = pathname.replace(/^\/tenant\/?/, '').split('/').filter(Boolean);
  const segments: BreadcrumbSegment[] = [{ label: 'Tenant', path: '/tenant/home' }];
  let current = '/tenant';
  for (const part of parts) {
    current += `/${part}`;
    segments.push({
      label: part.replace(/-/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase()),
      path: current,
    });
  }
  return segments;
}

function TenantLayoutShell() {
  useTimezone();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const session = getTenantSession();
  const { data: tenantMe } = useTenantMe();
  const { mode } = useTenantViewMode();
  const displayName = tenantMe?.name ?? session?.tenantName ?? 'Tenant';

  const navItems = useMemo(() => getTenantNavItems(mode), [mode]);

  const breadcrumb = useMemo(
    () => buildTenantBreadcrumb(location.pathname),
    [location.pathname],
  );

  return (
    <>
      <SkipLink />
      <AppShell
        portal="tenant"
        navItems={navItems}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        header={
          <GlobalHeader
            portal="tenant"
            title={displayName}
            onMenuToggle={() => setMobileOpen(true)}
            headerActions={
              <>
                <HelpHeaderButton />
                <ViewModeToggle />
              </>
            }
            user={{
              id: tenantMe?.id ?? session?.tenantId ?? 'tenant',
              displayName,
              email: tenantMe?.email ?? undefined,
              portal: 'tenant',
            }}
            onLogout={() => {
              logoutTenant();
              navigate('/public/login?portal=tenant');
            }}
          />
        }
      >
        <Outlet context={{ breadcrumb }} />
      </AppShell>
      <MobileBottomNav />
    </>
  );
}

export function TenantLayout() {
  return (
    <TenantAuthProvider>
      <TenantViewModeProvider>
        <TenantLayoutShell />
      </TenantViewModeProvider>
    </TenantAuthProvider>
  );
}
